import React, { useEffect, useCallback, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores";
import { WhatsAppWebRTCService } from "./services/WhatsAppWebRTCService";
import { WhatsAppCallModal } from "./modals/WhatsAppCallModal";
import { IncomingCallModal } from "./modals/IncomingCallModal";
import { RealTimeEvent, WhatsAppCallEventPayload } from "../../models";
import { toast } from "bulma-toast";

export const WhatsAppCallProvider: React.FC = observer(() => {
  const bots = useStore("bots");
  const selectedBot = bots.selectedBot;
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isTerminating, setIsTerminating] = useState(false);
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);

  const webrtcService = React.useMemo(() => {
    return new WhatsAppWebRTCService(selectedBot ?? ({} as any));
  }, [selectedBot]);

  // Initialize ringtone audio
  useEffect(() => {
    ringtoneRef.current = new Audio('/assets/whatsapp_ringtone.mp3');
    ringtoneRef.current.loop = true;
    ringtoneRef.current.volume = 0.7;
    
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
    };
  }, []);

  // Ringtone control functions
  const playRingtone = useCallback(async () => {
    if (ringtoneRef.current) {
      try {
        await ringtoneRef.current.play();
      } catch (error) {
        console.log("Could not play ringtone:", error);
        // Fallback: try to play after user interaction
      }
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  // Listen for WhatsApp call events through custom events
  useEffect(() => {
    if (!bots.selectedBot?.settings?.whatsappCallEnabled) return;

    const handleWhatsAppCallEvent = (event: CustomEvent) => {
      const realTimeEvent = event.detail as RealTimeEvent;
      const payload = realTimeEvent.params as WhatsAppCallEventPayload;

      switch (realTimeEvent.name) {
        case "whatsapp_call.incoming":
          bots.setIncomingWhatsAppCall({
            callId: payload.call_id,
            from: payload.from,
            to: payload.to,
            sdpOffer: payload.session?.sdp || "",
            timestamp: payload.timestamp,
            subscriberId: payload.subscriber_id,
          });
          // Play ringtone for incoming call
          playRingtone();
          break;

        case "whatsapp_call.connected":
          bots.setWhatsAppConnectionState("connected");
          if (bots.whatsappCall.currentCall) {
            bots.setCurrentWhatsAppCall({
              ...bots.whatsappCall.currentCall,
              status: "connected",
            });
          }
          break;

        case "whatsapp_call.ended":
          bots.setCurrentWhatsAppCall(null);
          bots.setWhatsAppConnectionState("closed");
          webrtcService.cleanup();
          // Stop ringtone when call ends
          stopRingtone();
          break;

        case "whatsapp_call.failed":
          if (bots.whatsappCall.currentCall) {
            bots.setCurrentWhatsAppCall({
              ...bots.whatsappCall.currentCall,
              status: "failed",
            });
          }
          bots.setWhatsAppConnectionState("failed");
          // Stop ringtone on call failure
          stopRingtone();
          break;
      }
    };

    window.addEventListener(
      "whatsapp_call_event",
      handleWhatsAppCallEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "whatsapp_call_event",
        handleWhatsAppCallEvent as EventListener
      );
    };
  }, [bots, webrtcService, playRingtone, stopRingtone]);

  const handleAcceptCall = useCallback(async () => {
    const incomingCall = bots.whatsappCall.incomingCall;
    if (!incomingCall) return;

    // Stop ringtone immediately when accepting call
    stopRingtone();
    
    setIsAccepting(true);
    try {
      // Set current call state
      bots.setCurrentWhatsAppCall({
        callId: incomingCall.callId,
        from: incomingCall.from,
        to: incomingCall.to,
        direction: "inbound",
        status: "connecting",
        startedAt: new Date(),
        duration: 0,
      });

      // Clear incoming call
      bots.setIncomingWhatsAppCall(null);

      // Accept call via WebRTC service
      await webrtcService.acceptCall(incomingCall, {
        onConnectionStateChange: bots.setWhatsAppConnectionState,
        onCallEnded: () => {
          bots.setCurrentWhatsAppCall(null);
          bots.setWhatsAppConnectionState("closed");
        },
      });
    } catch (error) {
      console.error("Error accepting WhatsApp call:", error);
      bots.setIncomingWhatsAppCall(null);
      bots.setCurrentWhatsAppCall(null);
      toast({
        message: "Failed to accept WhatsApp call. Please try again.",
        position: "top-right",
        dismissible: true,
        type: "is-danger",
        pauseOnHover: true,
      });
    } finally {
      setIsAccepting(false);
    }
  }, [bots, webrtcService, stopRingtone]);

  const handleRejectCall = useCallback(async () => {
    const incomingCall = bots.whatsappCall.incomingCall;
    if (!incomingCall) return;

    // Stop ringtone immediately when rejecting call
    stopRingtone();
    
    setIsRejecting(true);
    try {
      await webrtcService.rejectCall(incomingCall.callId);
    } catch (error) {
      console.error("Error rejecting WhatsApp call:", error);
      toast({
        message: "Failed to reject WhatsApp call. Please try again.",
        position: "top-right",
        dismissible: true,
        type: "is-danger",
        pauseOnHover: true,
      });
    } finally {
      setIsRejecting(false);
      bots.setIncomingWhatsAppCall(null);
    }
  }, [bots, webrtcService, stopRingtone]);

  const handleTerminateCall = useCallback(async () => {
    const currentCall = bots.whatsappCall.currentCall;
    if (!currentCall) return;

    setIsTerminating(true);
    try {
      await webrtcService.terminateCall(currentCall.callId);
      bots.setCurrentWhatsAppCall(null);
      bots.setWhatsAppConnectionState("closed");
    } catch (error) {
      console.error("Error terminating WhatsApp call:", error);
      toast({
        message: "Failed to terminate WhatsApp call. Please try again.",
        position: "top-right",
        dismissible: true,
        type: "is-danger",
        pauseOnHover: true,
      });
    } finally {
      setIsTerminating(false);
    }
  }, [bots, webrtcService]);

  // Don't render if WhatsApp calls are not enabled
  if (!bots.selectedBot?.settings?.whatsappCallEnabled) {
    return null;
  }

  return (
    <>
      {/* Incoming call modal */}
      {bots.whatsappCall.incomingCall && (
        <IncomingCallModal
          caller={bots.whatsappCall.incomingCall.from}
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
          isAccepting={isAccepting}
          isRejecting={isRejecting}
        />
      )}

      {/* Current call modal */}
      {bots.whatsappCall.currentCall && (
        <WhatsAppCallModal
          call={bots.whatsappCall.currentCall}
          connectionState={bots.whatsappCall.connectionState}
          onTerminate={handleTerminateCall}
          isTerminating={isTerminating}
        />
      )}
    </>
  );
});
